import { useEffect, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { X } from "lucide-react"
import {
  languageChoiceMade,
  localeSwitchTarget,
  rememberLanguageChoice,
} from "@/lib/locale-path"
import type { PageProps } from "@/types/inertia"

// Offers the Spanish site to a visitor whose browser is set to Spanish and who
// has landed on an English page — the whole estate exists in both languages,
// and the only thing pointing at that today is a toggle in the corner of the
// nav. It appears once: taking it, dismissing it, or using the toggle all
// count as a choice, and the choice is remembered in this browser.
//
// The preferred language is read from the browser rather than passed down from
// Accept-Language, which is the same setting and keeps the bar out of the
// server-rendered markup — nothing to mismatch on hydration, and a crawler
// never sees it.
export function LanguageOffer() {
  const { current_locale, locale_slugs } = usePage<PageProps>().props
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (current_locale !== "en") return
    if (languageChoiceMade()) return

    const languages = navigator.languages?.length ? navigator.languages : [navigator.language]
    const prefersSpanish = languages.some((tag) => tag?.toLowerCase().startsWith("es"))
    if (prefersSpanish) setShow(true)
  }, [current_locale])

  if (!show) return null

  const accept = () => {
    rememberLanguageChoice("es")
    setShow(false)
    const { path, query } = localeSwitchTarget("es", window.location.pathname, locale_slugs)
    router.get(path, query, { preserveScroll: true, preserveState: false })
  }

  const dismiss = () => {
    rememberLanguageChoice("en")
    setShow(false)
  }

  return (
    // Bottom left: the assistant's button sits bottom right, and a bar across
    // the top would have to negotiate with a fixed nav and the admin bar.
    <div
      lang="es"
      role="region"
      aria-label="Idioma del sitio"
      className="fixed bottom-4 left-4 z-40 max-w-[calc(100vw-2rem)] sm:max-w-sm"
    >
      <div className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-lg ring-1 ring-hairline">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink-display">¿Prefieres español?</p>
          <p className="mt-1 text-sm text-ink-muted">
            Esta página también está en español, igual que el resto del sitio.
          </p>
          <button
            onClick={accept}
            className="mt-3 rounded-full bg-teal px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            Ver en español →
          </button>
        </div>
        <button
          onClick={dismiss}
          aria-label="Seguir en inglés"
          className="-mt-1 -mr-1 shrink-0 rounded-full p-1.5 text-ink-muted transition-colors hover:bg-surface hover:text-ink-body"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
